import { Injectable, Type } from '@angular/core';
import { hasStoredAccessToken } from '../../user/auth-tokens';
import { MapModalData } from '../../user/map-redirect-modal/map-redirect-modal.component';
import { SubscriptionPlans } from '../../user/user/user.model';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { modalRegistry } from './modal-registry';
import { CATEGORY_TO_PLAN, USER_CATEGORY_KEY, UserCategory } from '../../map/user-guide/user-guide.service';
import { BreakpointObserverService } from './breakpoint-observer.service';
import { SignUpTrackingService } from '../../user/sign-up-tracking.service';
import { SubscriptionsMenuComponent } from '../../user/user/subscriptions-menu/subscriptions-menu.component';
import type { HelpAndFeedbackModalData } from '../components/help-and-feedback-modal/help-and-feedback-modal.component';

/**
 * Which paywall the `sign_up` event is credited to when an anonymous visitor
 * is gated on their way into a purchase. One string for all four entry
 * points: they now share a single screen after the click.
 */
export const SUBSCRIPTION_PURCHASE_PLACEMENT = 'subscription-purchase';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  public isModalOpen = false;

  constructor(
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserverService,
    private signUpTracking: SignUpTrackingService
  ) {}

  public async openModal(text: MapModalData): Promise<void> {
    // Check if modal is already opened otherwise it could open in multiple instances
    if (this.isModalOpen) return;

    // Claimed before the await, or two callers racing the chunk load would
    // each open a dialog. That makes the failure path load-bearing: the flag
    // is only cleared in afterClosed(), so a chunk that never arrives would
    // otherwise leave it set and no modal could open again in this document.
    this.isModalOpen = true;

    let modal: Type<unknown>;
    try {
      modal = await modalRegistry.MapRedirectModal();
    } catch (error) {
      this.isModalOpen = false;
      // lazyChunk logs its retries at `warn` and leaves the verdict to the
      // caller; a dialog the visitor asked for and did not get is an issue.
      console.error('[modal] MapRedirectModal failed to load', error);
      return;
    }

    const dialogRef = this.dialog.open(modal, {
      panelClass: 'custom-dialog-container',
      disableClose: text.disableClose,
      hasBackdrop: text.hasBackdrop,
      maxWidth: '620px',
      data: text,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.isModalOpen = false;
    });
  }

  public async openHelpAndFeedbackModal(initialMessage?: string): Promise<void> {
    this.dialog.open(await modalRegistry.HelpAndFeedbackModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: `${Math.min(800, window.innerWidth - 32)}px`,
      data: { message: initialMessage } as HelpAndFeedbackModalData,
    });
  }



  /**
   * The last screen before Stripe. Not public: a purchase always starts at
   * startSubscriptionPurchase below, which puts the sign-up gate in front of
   * this.
   *
   * No coverage region is passed. It is no longer a choice - the plans that
   * need one are all sold nationwide - so the modal derives it from the plan
   * itself, next to the request that sends it. Reading the plan description
   * here instead would give this service a runtime import of user.model,
   * which closes a cycle back through the map's layer store.
   */
  private async handleSubscriptionConfirmationModal(
    plan: SubscriptionPlans,
    isPeriodMonthly: boolean,
    freePeriodString: string | null,
    promoCode: string | null,
  ): Promise<void> {
    this.subscriptionConfirmationModal = this.dialog.open(await modalRegistry.SubscriptionConfirmationModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: '620px',
      data: {
        plan,
        isPeriodMonthly,
        freePeriodString,
        promoCode,
      },
    });
  }

  private pendingAuthRequest: Promise<boolean> | null = null;

  /**
   * Opens the in-place sign-up / sign-in modal on top of whatever is
   * currently open (purchase flow, map paywalls, AI chat) so anonymous
   * users don't lose their context. Resolves once the modal is closed,
   * with the actual auth state at that moment — regardless of how the
   * modal was closed.
   *
   * Paywall triggers fire repeatedly (map rerenders, clicks), so while
   * an auth modal is already open all callers share it instead of
   * stacking new ones.
   */
  public requestAuth(options?: {
    headerText?: string;
    subHeaderText?: string;
    initialMode?: 'options' | 'signup' | 'signin';
    /**
     * Which paywall made the visitor sign up, reported with the `sign_up`
     * event. Only known here: by the time the account exists, the gate that
     * demanded it is closed and forgotten.
     */
    placement?: string;
  }): Promise<boolean> {
    if (this.pendingAuthRequest) {
      return this.pendingAuthRequest;
    }

    this.pendingAuthRequest = (async () => {
      try {
        if (options?.placement) {
          this.signUpTracking.gateOpened(options.placement);
        }
        const ref = this.dialog.open(await modalRegistry.AuthModal(), {
          panelClass: 'custom-dialog-container',
          disableClose: false,
          maxWidth: '600px',
          autoFocus: false,
          data: options ?? null,
        });
        await firstValueFrom(ref.afterClosed());
        return hasStoredAccessToken();
      } finally {
        this.signUpTracking.gateClosed();
        this.pendingAuthRequest = null;
      }
    })();

    return this.pendingAuthRequest;
  }

  private subscriptionConfirmationModal?: MatDialogRef<unknown>;
  // Covers the stretch of startSubscriptionPurchase where there is no
  // dialog of ours to observe - the sign-up gate and the confirmation
  // modal's chunk load. Without it a paywall firing in that window stacks
  // the upsell on top of the gate. See openSubscriptionsModal.
  //
  // A count, not a flag: paywall buttons are clickable more than once, and
  // two overlapping flows share one auth modal (requestAuth dedupes), so
  // the first to finish must not clear the guard out from under the second.
  private openPurchaseFlows = 0;

  /**
   * The single way into buying a subscription, from all four paywalls: the
   * profile plan cards, /pricing, the recommendation modal and the
   * sales-forecast pages.
   *
   * Gates on an account, then opens the confirmation modal. There used to be
   * a coverage-area step between the click and the gate; every subscription
   * now covers the whole country, so the visitor goes straight to what they
   * are about to pay.
   *
   * Each caller reports the click itself, before calling this: they already
   * hold the analytics services, and importing GoogleAnalyticsService here
   * closes a module cycle (google-analytics -> user.model -> layer-store ->
   * ... -> modal.service) that breaks module initialisation in the main
   * bundle.
   *
   * @returns whether the confirmation modal was reached - false only when an
   *   anonymous visitor closed the sign-up gate without an account. Lets a
   *   caller that is itself a modal (the recommendation upsell) hold its own
   *   screen until the purchase is genuinely under way, instead of closing
   *   into nothing when the gate is dismissed.
   */
  public async startSubscriptionPurchase(options: {
    plan: SubscriptionPlans;
    isPeriodMonthly: boolean;
    promoCode?: string | null;
    freePeriodString?: string | null;
  }): Promise<boolean> {
    const {
      plan,
      isPeriodMonthly,
      promoCode = null,
      freePeriodString = null,
    } = options;

    this.openPurchaseFlows += 1;
    try {
      // Sign up / log in on top of whatever opened this, so the purchase
      // context survives; on success continue straight to the confirmation.
      if (!hasStoredAccessToken()) {
        const authenticated = await this.requestAuth({
          subHeaderText: 'An account keeps what you paid for yours.',
          placement: SUBSCRIPTION_PURCHASE_PLACEMENT,
        });
        if (!authenticated) return false;
      }

      await this.handleSubscriptionConfirmationModal(
        plan,
        isPeriodMonthly,
        freePeriodString,
        promoCode
      );
      return true;
    } finally {
      // Released only once the confirmation modal is open (or the flow gave
      // up), so isSubscriptionConfirmationModalOpen below takes over with no
      // gap in between.
      this.openPurchaseFlows -= 1;
    }
  }

  private isSubscriptionConfirmationModalOpen(): boolean {
    return this.subscriptionConfirmationModal?.getState() === MatDialogState.OPEN;
  }

  public async handleSubscriptionRenewalModal(
    isAutoRenewable: boolean
  ): Promise<void> {
    this.dialog.open(await modalRegistry.SubscriptionRenewalModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: '620px',
      data: {
        isAutoRenewable: isAutoRenewable,
      },
    });
  }

  public async handleSetRadiusModal(radius: number): Promise<void> {
    this.dialog.open(await modalRegistry.SetRadiusModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: '560px',
      maxHeight: '275px',
      data: { radius: radius },
    });
  }

  private subscriptionsModal?: MatDialogRef<SubscriptionsMenuComponent>;
  // Guards the async gap while the modal chunk is being lazy-loaded:
  // the getState() check below can't see a dialog that isn't open yet,
  // and paywall triggers fire repeatedly (map rerenders, clicks)
  private isSubscriptionsModalOpening = false;

  public closeSubscriptionsModal(): void {
    this.subscriptionsModal?.close();
  }

  public async openSubscriptionsModal(mode: "all_packages" | "ai_packages", userCategory?: UserCategory): Promise<void> {
    if (
      this.isSubscriptionsModalOpening ||
      (this.subscriptionsModal &&
        this.subscriptionsModal.getState() === MatDialogState.OPEN)
    ) {
      return;
    }

    // User is already somewhere in a purchase (the sign-up gate or the
    // confirmation modal, both deeper in the flow) — don't stack the
    // subscription upsell on top of it
    if (
      this.openPurchaseFlows > 0 ||
      this.isSubscriptionConfirmationModalOpen()
    ) {
      return;
    }

    this.isSubscriptionsModalOpening = true;
    try {
      const category = userCategory ?? localStorage.getItem(USER_CATEGORY_KEY) as UserCategory | null;
      const recommendedPlan = category ? CATEGORY_TO_PLAN[category] : undefined;

      if (recommendedPlan) {
        const res = this.dialog.open(await modalRegistry.RecommendedSubscriptionModal(), {
          panelClass: 'custom-dialog-container',
          disableClose: false,
          maxWidth: '600px',
          data: { recommendedPlan, mode },
        });
        this.subscriptionsModal = res as any;
        return;
      }

      const isMobile = this.breakpointObserver.isMobile.value;
      const res = this.dialog.open(await modalRegistry.SubscriptionPlansModal(), {
        panelClass: 'custom-dialog-container',
        disableClose: false,
        // Wide enough for all four plans side by side, and never taller than
        // the window - the modal scrolls its own plan grid instead. On mobile
        // the container sizes itself in dvh/vw, so only the caps are set here.
        ...(isMobile ? {} : { width: '94vw' }),
        maxWidth: isMobile ? '800px' : '1280px',
        maxHeight: isMobile ? '1200px' : '92vh',
        data: { mode },
      });
      this.subscriptionsModal = res;
    } finally {
      this.isSubscriptionsModalOpening = false;
    }
  }
}
